<template>
    <v-container>
        <v-layout row wrap align-center>
            <v-flex xs12 md6>
                <v-btn :to="{ name: 'SittingDayStatistics'}" color="white">
                    <v-icon dark left>arrow_back</v-icon>Back to Sitting days
                </v-btn>
            </v-flex>
        </v-layout>
        <v-layout row wrap pt-3 pb-4 align-center>
            <v-flex xs12 md6>
                <h1>Sitting days for <router-link v-if="showBack" :to="{ params: { year: parseInt(this.year) - 1 } }"><v-icon>arrow_back</v-icon></router-link>{{year}}
                <router-link v-if="showForward" :to="{ params: { year: parseInt(this.year) + 1 } }"><v-icon>arrow_forward</v-icon></router-link></h1>
            </v-flex>
        </v-layout>

        <loader v-if="loading" />
        <template v-if="!loading" >
            <v-container grid-list-xl>
                <v-layout row wrap>
                    <v-flex v-for="month in calendar" :key="month.name" xs2>
                        <div class="sitting-day-calendar__month"><h4>{{ month.name }}</h4></div>
                        <table class="sitting-day-calendar">
                            <thead><tr><th class="setwidth">Mon</th><th class="setwidth">Tue</th><th class="setwidth">Wed</th><th class="setwidth">Thu</th><th class="setwidth">Fri</th><th class="setwidth">Sat</th><th class="setwidth">Sun</th></tr></thead>
                            <tbody>
                                <tr v-for="(week, index) in month.rows" :key="index">
                                    <td v-for="day in week" :key="day.date.toString()" 
                                        :class="{
                                            'both-sitting': day.repsSitting && day.senateSitting && !day.inactive,
                                            'house-sitting': day.repsSitting && !day.senateSitting && !day.inactive,
                                            'senate-sitting': day.senateSitting && !day.repsSitting && !day.inactive,
                                            'senate-estimates': day.senateEstimates && !day.inactive
                                        }">
                                        <div>
                                            <span v-if="!day.inactive">{{ day.date.date() }}</span>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </v-flex>
                </v-layout>
            </v-container>

            <div class="calendar-legend">
                <div><div style="background: #ffd7d7; width: 20px; height: 20px; float: left;"></div>&nbsp;&nbsp;<span>Senate sitting</span></div>
                <div><div style="background: #b1f9b1; width: 20px; height: 20px; float: left;"></div>&nbsp;&nbsp;<span>Reps sitting</span></div>
                <div><div style="background: #a7ddff; width: 20px; height: 20px; float: left;"></div>&nbsp;&nbsp;<span>Both sitting</span></div>
                <div><div style="width: 20px; height: 20px; float: left; border-radius: 15px; box-shadow: inset 0 0 0 1px #b9b9b9;"></div>&nbsp;&nbsp;<span>Senate estimates</span></div>
            </div>

            <br/>

            <v-layout row wrap>
                <v-flex xs12>
                    <v-btn class="csvButton" @click="exportCsv" v-on:keyup.enter="exportCsv" color="primary">
                        <v-icon aria-hidden="true" :class="csvDownloadClass">{{csvDownloadIcon}}</v-icon>
                        <span>&nbsp;Download as CSV</span>
                    </v-btn>
                </v-flex>
            </v-layout>         

        </template>
    </v-container>
</template>
<script>
import Loader from '@/components/Loader.vue';
import axios from 'axios';
import moment from 'moment'

function getAllDaysInMonth(year, month) {
  const date = new Date(year, month, 1);

  const dates = [];

  while (date.getMonth() === month) {
    dates.push(new Date(date));
    date.setDate(date.getDate() + 1);
  }

  return dates;
}

export default {
    created() {
        this.initialiseItems();
    },
    components: {
        Loader
    },
    props: ['year'],
    data() {
        return {
            items: [],
            loading: true,
            csvDownloading: false
        }
    },
    methods: {
        initialiseItems() {
            this.loading = true;
            const url = `StatisticalInformation/SittingDaysForYear?year=${this.year}`;
            axios.get(`api/${url}`)
                .then(response => {
                    this.items = response.data;
                    this.loading = false;
                });
        },
        exportCsv: function () {
            this.csvDownloading = true;
            const records = this.items.map(i => 
                ({ 
                    Date: (new Date(i.Date)).toLocaleDateString(),
                    'House sitting': i.RepsSitting,
                    'Senate sitting': i.SenateSitting,
                    'Estimates': i.SenateEstimates
                }));
            let headers = ['Date', 'House sitting', 'Senate sitting', 'Estimates'];
            const filename = `Sitting days for ${this.year}`;
            const comment = `Data current as of ${(new Date()).toLocaleDateString()}`;
            this.$store.dispatch('GETCSV', { DATA: records, HEADERS: headers, FILENAME: filename, COMMENT: comment })
                .catch(this.showError)
                .finally(() => this.csvDownloading = false);
        }
    },
    computed: {
        csvDownloadIcon() {
            return this.csvDownloading ? 'cached' : 'text_snippet';
        },
        csvDownloadClass() {
            let csvClass = 'mr-1';
            if (this.csvDownloading){
                csvClass = csvClass + ' loading'
            }
            return csvClass;
        },
        title() {
            return `Sitting days for ${this.year}`
        },
        showBack() {
            return this.year > 1901;
        },
        showForward() {
            return this.year < new Date().getFullYear();
        },
        calendar() {
            // get a full calendar of days. 
            const calendar = [];
            for(let i = 0;i <= 11;i++) {
                // get rows
                // start from 1st of the month, 
                // keep going until last day of the week of the last day of the month is reached.
                const daysInMonth = getAllDaysInMonth(this.year, i);
                const firstDay = moment(daysInMonth[0]).startOf('isoWeek');
                const lastDay = moment(daysInMonth[daysInMonth.length - 1]).endOf('week');
                const month = new Date(this.year, i, 1);
                const monthName = month.toLocaleString('default', { month: 'long' });

                let j = firstDay;
                let rows = [];
                let row = [];
                while(j <= lastDay) {
                    row.push({
                        date: moment(j),
                        repsSitting: this.items.findIndex(i => moment(i.Date).isSame(j, 'day') && i.RepsSitting) !== -1,
                        senateSitting: this.items.findIndex(i => moment(i.Date).isSame(j, 'day') && i.SenateSitting) !== -1,
                        senateEstimates: this.items.findIndex(i => moment(i.Date).isSame(j, 'day') && i.SenateEstimates) !== -1,
                        inactive: !j.isSame(month, 'month')
                    })
                    if (j.isoWeekday() == 7 || j.isSame(lastDay, 'day')) {
                        rows.push(row);
                        row = [];
                    }
                    j.add(1, 'day');
                }

                calendar.push({ name: monthName, rows });
            }
            return calendar
        }
    }
}
</script>
<style scoped>
    .sitting-day-calendar {
        text-align: center;
        width: 90%;
        margin: auto;
        background: white;
        border: solid 1px #ddd;
        border-collapse: collapse;
    }
    .sitting-day-calendar td {
        border: solid 1px #ddd;
        height: 32px;
        width: 32px;
    }
    .house-sitting {
        background-color: #b1f9b1;
    }
    .senate-sitting {
       background-color: #ffd7d7; 
    }
    .both-sitting {
        background-color: #a7ddff;
    }
    .senate-estimates div {
        width: 28px !important;
        height: 28px !important;
        border-radius: 15px;
        box-shadow: inset 0 0 0 1px #b9b9b9;
        display: inline-block;
        line-height: 30px;
    }
    .sitting-day-calendar__month {
        text-align: center;
        background-color: #325986;
        width: 90%;
        margin: auto;
        padding: .75rem 0 .75rem;
        color: white;
    }
    .sitting-day-calendar thead {
        background: #f5f5f5;
    }
    .grid-list-xl {
        padding-left: 0;
        padding-right: 0;
    }
    .calendar-legend > div {
        clear: both;
        padding-top: 5px;
        padding-bottom: 5px;
    }
    .v-icon {
        vertical-align: middle;
    }
</style>
